// Las filas de una pestaña de Comisiones pagadas: del corte en curso en "Esta semana"; en los demás
// periodos, los cortes cerrados del rango más el corte en curso y el anterior mientras no se cierra.
// Las fuentes salen con la misma forma.
import type { RetailCommission } from '@fiado/type-kit';
import { addDays, periodRange, weekStart, type PayoutPeriod } from '../lib/payoutPeriods';
import {
    aggregateBreakdowns,
    inexactSellerKeys,
    mergeAggregateRows,
    type AggregateBy,
} from '../lib/payoutRows';
import { useLivePayout, usePayoutAggregates } from './commissionQueries';

export interface PayoutRows {
    rows: RetailCommission.CommissionAggregateRow[];
    totalPaidCents: number;
    /** Los cortes que entraron: los cerrados del rango, el anterior sin cerrar y el en curso. */
    periodIds: string[];
    isLoading: boolean;
    /** Las fuentes respondieron sin error: ya se puede elegir la primera fila. */
    isReady: boolean;
    error: Error | null;
    refetch: () => void;
    /** `live`: solo el corte en curso; `range`: los cerrados del rango más los que van en vivo. */
    source: 'live' | 'range';
    /**
     * Por regla, tienda o SKU: las claves que estuvieron en los cortes cerrados y en el en curso. Su
     * `sellersCount` es "al menos": no se sabe qué vendedores se repiten entre los dos.
     */
    sellersAtLeast: ReadonlySet<string>;
    /** El corte en curso llegó pero no entra en el periodo: sus vendedores no suman en las filas. */
    liveOutsidePeriod: boolean;
}

const NO_KEYS: ReadonlySet<string> = new Set();

const sumPaid = (rows: readonly RetailCommission.CommissionAggregateRow[]) =>
    rows.reduce((sum, row) => sum + row.paidCents, 0);

export function usePayoutRows(by: AggregateBy, period: PayoutPeriod, today: string): PayoutRows {
    const range = periodRange(period, today);
    const live = useLivePayout();
    const closed = usePayoutAggregates(by, range?.from ?? null, range?.to ?? null);

    // Los agregados solo suman cortes cerrados: lo que va de la semana sale del corte vivo. Si ese
    // corte ya aparece entre los cerrados, no se suma otra vez.
    const closedPeriodIds = closed.data?.periodIds ?? [];
    const current =
        live.data &&
        (range === null ||
            (live.data.periodId >= range.from &&
                live.data.periodId <= range.to &&
                !closedPeriodIds.includes(live.data.periodId)))
            ? live.data
            : null;
    // La semana anterior, mientras no se cierra, también va en vivo, como en Mis comisiones.
    const previousId = addDays(live.data?.periodId ?? weekStart(today), -7);
    const previousOpen =
        range !== null &&
        closed.data !== undefined &&
        previousId >= range.from &&
        previousId <= range.to &&
        !closedPeriodIds.includes(previousId);
    const previous = useLivePayout(previousId, previousOpen);
    const previousData = previousOpen ? previous.data : undefined;
    // Las dos semanas en vivo van juntas: así un vendedor que vendió en ambas cuenta una vez.
    const liveRows = aggregateBreakdowns(
        [...(previousData?.sellers ?? []), ...(current?.sellers ?? [])],
        by,
    );
    const liveReady = live.data !== undefined && live.error === null;
    const previousReady = !previousOpen || (previous.data !== undefined && previous.error === null);
    const liveOutsidePeriod = live.data !== undefined && current === null;

    if (range === null) {
        return {
            rows: liveRows,
            totalPaidCents: sumPaid(liveRows),
            periodIds: current ? [current.periodId] : [],
            isLoading: live.isLoading,
            isReady: liveReady,
            error: live.error,
            refetch: () => {
                void live.refetch();
            },
            source: 'live',
            sellersAtLeast: NO_KEYS,
            liveOutsidePeriod,
        };
    }
    const closedRows = closed.data?.rows ?? [];
    return {
        rows: mergeAggregateRows(closedRows, liveRows),
        totalPaidCents: (closed.data?.totalPaidCents ?? 0) + sumPaid(liveRows),
        periodIds: [
            ...closedPeriodIds,
            ...(previousData ? [previousId] : []),
            ...(current ? [current.periodId] : []),
        ],
        isLoading: closed.isLoading || live.isLoading || (previousOpen && previous.isLoading),
        isReady: liveReady && previousReady && closed.data !== undefined && closed.error === null,
        error: closed.error ?? live.error ?? (previousOpen ? previous.error : null),
        refetch: () => {
            void closed.refetch();
            void live.refetch();
            if (previousOpen) void previous.refetch();
        },
        source: 'range',
        // Por vendedor la clave es el vendedor: su conteo siempre es exacto.
        sellersAtLeast: by === 'SELLER' ? NO_KEYS : inexactSellerKeys(closedRows, liveRows),
        liveOutsidePeriod,
    };
}
